import type { CandidateMode, MealType, RatingValue, RetailerId } from "./types";

import { BARCODE_FAMILY_VALUES, MATCH_PROVENANCE_VALUES, type BarcodeFamily, type MatchProvenance } from "./intake-contracts";

export type { CandidateMode, MealType, RatingValue, RetailerId };
export type IngredientCategory =
  | "produce"
  | "dairy"
  | "meat"
  | "seafood"
  | "bakery"
  | "pantry"
  | "frozen"
  | "beverages"
  | "condiments"
  | "spices"
  | "other";
export type FeedbackContext =
  "meal_plan" | "grocery_list" | "recipe" | "general";
export type PmfSurveyAnswer =
  "very_disappointed" | "somewhat_disappointed" | "not_disappointed";
export type BetaClaimKind = "code" | "household";

export type AuthMethod = "email" | "google";
export type RecipeViewSource =
  "plan" | "browse" | "search" | "favorites" | "hero" | "direct" | "created";
export type PlanFailureReason =
  | "validation_failed"
  | "unauthorized"
  | "conflict"
  | "network_error"
  | "server_error"
  | "unknown";
export type ImportFailureReason =
  | "unsupported_page"
  | "extraction_failed"
  | "rate_limited"
  | "network_error"
  | "server_error"
  | "unknown";
export type InviteClaimFailureReason =
  | "invalid_code"
  | "already_used"
  | "beta_full"
  | "rate_limited"
  | "network_error"
  | "server_error"
  | "unknown";
export type AnalyticsCohort = "external" | "internal" | "test";
export type AnalyticsView =
  | "dashboard"
  | "recipes"
  | "groceries"
  | "profile"
  | "settings"
  | "meal-planner"
  | "pantry"
  | "preferences"
  | "admin-users"
  | "admin-beta-invites"
  | "admin-feedback"
  | "recipe-detail"
  | "onboarding"
  | "create-recipe"
  | "cook-mode";

interface GroceryItemTelemetry {
  ingredientId: string;
  unit: string;
  category: IngredientCategory;
  quantity: number | null;
  sourceRecipeCount: number;
}

interface RecommendationTelemetry {
  /** Random per-flow identifier, never an account/household ID or fingerprint. */
  sessionId: string;
  algorithmVersion: string;
}

export interface AnalyticsEventProperties {
  plan_recommendation_batch_shown: RecommendationTelemetry & {
    mealType: MealType;
    batchIndex: number;
    /** Choices offered on the active step; this does not claim viewport visibility. */
    recipeIds: string[];
    eligibleCount: number;
    unseenCount: number;
    freshCount: number;
    pantryMatchCount: number;
    selectedCount: number;
    exhausted: boolean;
  };
  plan_recommendation_selection_changed: RecommendationTelemetry & {
    mealType: MealType;
    recipeId: string;
    position: number;
    selected: boolean;
    pantryOverlap: boolean;
  };
  plan_recommendation_committed: RecommendationTelemetry & {
    mealPlanId: string;
    recipeIds: string[];
  };
  signed_up: { method: "email" | "google" };
  signed_in: { method: AuthMethod };
  onboarding_started: undefined;
  onboarding_completed: { skipped: boolean };
  plan_generation_started: {
    mealTypeCount: number;
    requestedSlotCount: number;
    isFirstPlan: boolean;
  };
  plan_generated: {
    mealTypes: Array<"breakfast" | "lunch" | "dinner" | "snacks">;
    slotCount: number;
    emptySlotCount: number;
    isFirstPlan: boolean;
  };
  plan_generation_failed: { status: number; reason: PlanFailureReason };
  plan_preview_generated: {
    mode: CandidateMode;
    enabledMealTypeCount: number;
    targetCount: number;
    returnedCount: number;
  };
  plan_preview_refreshed: {
    mealType: MealType;
    selectedCount: number;
    freshCount: number;
    exhausted: boolean;
    proteinFilter: string[];
  };
  plan_candidate_menu_committed: {
    mode: CandidateMode;
    selectedCount: number;
    openCount: number;
    mealTypeCount: number;
  };
  plan_candidate_menu_dismissed: {
    lastStep: "configure" | MealType | "review";
    selectedCount: number;
  };
  single_meal_suggestion_completed: {
    outcome: "added" | "no_match" | "occupied";
    mealType: MealType;
    hasCuisineScope: boolean;
  };
  recipe_viewed: { recipeId: string; source: RecipeViewSource };
  recipe_imported: { source: "jsonld" | "llm" };
  recipe_import_failed: { status: number; reason: ImportFailureReason };
  grocery_list_opened: { itemCount: number };
  grocery_list_cleared: { itemCount: number };
  grocery_list_completed: { itemCount: number; mealPlanId: string };
  grocery_item_toggled: { purchased: boolean };
  grocery_item_removed: GroceryItemTelemetry;
  grocery_item_restored: GroceryItemTelemetry;
  grocery_item_quantity_edited: GroceryItemTelemetry & {
    derivedQuantity: number;
    chosenQuantity: number;
    // The unit picker's selection for a previously-unquantified line
    // (#1023); equal to `unit` above for a line that already had one.
    chosenUnit: string;
    hadPreviousOverride: boolean;
  };
  grocery_item_added: {
    ingredientId: string;
    unit: string;
    quantity: number;
    category: IngredientCategory;
    source: "pantry_low_stock";
  };
  meal_swapped: { mealType: MealType };
  dashboard_hero_viewed: { state: "planned" | "empty"; stale: boolean };
  dashboard_hero_swap_clicked: { mealType: MealType };
  meal_rated: { value: RatingValue; recipeId: string };
  meal_cooked: {
    mealPlanId: string;
    mealSlotId: string;
    recipeId: string;
    mealType: MealType;
  };
  favorite_toggled: { favorited: boolean };
  view_changed: { view: AnalyticsView; from: AnalyticsView | null };
  invite_claimed: { type: BetaClaimKind };
  invite_claim_failed: {
    type: BetaClaimKind;
    status: number;
    reason: InviteClaimFailureReason;
  };
  feedback_submitted: { context: FeedbackContext; view: AnalyticsView };
  pmf_survey_submitted: { answer: PmfSurveyAnswer };
  retailer_picker_opened: {
    uncheckedCount: number;
    configuredRetailers: RetailerId[];
  };
  retailer_selected: { retailer: RetailerId };
  retailer_linked: { retailer: RetailerId };
  retailer_link_failed: { retailer: RetailerId; reason: "denied" | "error" };
  match_review_viewed: {
    retailer: RetailerId;
    matchedCount: number;
    unmatchedCount: number;
    unquantifiedCount?: number;
  };
  match_swapped: { retailer: RetailerId };
  match_excluded: { retailer: RetailerId };
  cart_pushed: {
    retailer: RetailerId;
    addedCount: number;
    failedCount: number;
    unquantifiedCount?: number;
  };
  handoff_link_opened: {
    retailer: RetailerId;
    handoffKind: "cart";
    itemCount: number;
  };
  kroger_cart_pushed: {
    added: number;
    alreadyInCart: number;
    unmatched: number;
    unavailable: number;
    unquantifiedCount?: number;
  };
  kroger_cart_push_failed: {
    kind: "modality" | "quota" | "reconnect" | "other";
  };
  aisle_order_changed: { categoryCount: number };

  /**
   * Onboarding v3 (issue #831, #630): fires once per distinct first-run step,
   * so drop-off per step is measurable. `stepId` is a closed enum (see
   * ONBOARDING_STEP_ID_VALUES below) -- never a raw field value. Per §7 of
   * the 2026-09-08 onboarding-v3 design spec: NEVER protein, diet, allergy,
   * dislike, cuisine, cookDay, weeknightPace, or ZIP values, on this event
   * or any other -- counts and the closed stepId enum only.
   */
  onboarding_step_completed: {
    stepId: "safety" | "household" | "tastes" | "cooking" | "budget" | "review";
    stepIndex: number;
    answeredCount: number;
    skipped: boolean;
  };

  // ---- Epic #738 slice S2 (issue #742): pantry intake + recipe-import
  // review instrumentation. Counts and bounded enums only -- never a raw
  // ingredient/allergy/free-text name (see PRIVATE_PROPERTY_KEY below and
  // its guard against "renamedCount"-shaped false positives).
  pantry_scan_attempted:
    | { mode: "shelf" | "receipt" }
    | {
        mode: "barcode";
        cameraSupported: boolean;
        captureMethod: "camera" | "manual";
      };
  pantry_scan_succeeded:
    | { mode: "shelf" | "receipt"; itemCount: number }
    | {
        mode: "barcode";
        itemCount: number;
        /** S0 enum; "unknown" until S5 classifies the scanned code family. */
        codeFamily: BarcodeFamily;
        /** Which tier resolved the match (local cache / Kroger / FDC / PLU
         * table / none). Optional and currently NEVER populated -- today's
         * barcode-lookup response exposes no tier signal to derive this
         * from, and this slice does not change that response shape. A
         * follow-up (S4/S13/S14) both adds the signal server-side and wires
         * this. */
        resolvedVia?: "local" | "kroger" | "fdc" | "plu" | "none";
      };
  pantry_scan_failed: { mode: "shelf" | "receipt" | "barcode"; status: number };
  pantry_intake_reviewed: {
    mode: "shelf" | "receipt" | "barcode";
    acceptedCount: number;
    editedCount: number;
    /** Rows whose persisted ingredient identity changed
     * during review. Named "relabeledCount", NOT the issue's proposed
     * "renamedCount" -- see the guard test in analytics.test.ts for why. */
    relabeledCount: number;
    rejectedCount: number;
    /** Keyed by every MatchProvenance value (S0). Only t1_exact/unmatched
     * are non-zero until S10 lands t2_fuzzy/t3_llm_rerank matching --
     * "matched" is derived client-side today as `Boolean(ingredientId)`. */
    provenanceCounts: Record<MatchProvenance, number>;
  };
  recipe_import_reviewed: { acceptedCount: 1; editedCount: 0 | 1 };
  server_signup_blocked: {
    reason: "invite_required" | "invite_invalid" | "user_cap_reached";
  };
  server_plan_utilization_predicted: {
    mealPlanId: string;
    predictedSpoiledGrams: number | null;
    predictedCarryoverGrams: number | null;
    packCoverage: number | null;
    perishableCount: number;
    thisShopTotal: number | null;
    typicalWeekTotal: number | null;
    perServingTypical: number | null;
    reuseReasonSlotCount: number;
    calculationVersion: string;
  };
}

export type AnalyticsEvent = keyof AnalyticsEventProperties;
/** Runtime mirror used by source/privacy tooling; keep this adjacent to the typed contract. */
export const ANALYTICS_EVENT_PROPERTY_REQUIREMENTS: Record<
  AnalyticsEvent,
  boolean
> = {
  plan_recommendation_batch_shown: true,
  plan_recommendation_selection_changed: true,
  plan_recommendation_committed: true,
  signed_up: true,
  signed_in: true,
  onboarding_started: false,
  onboarding_completed: true,
  plan_generation_started: true,
  plan_generated: true,
  plan_generation_failed: true,
  plan_preview_generated: true,
  plan_preview_refreshed: true,
  plan_candidate_menu_committed: true,
  plan_candidate_menu_dismissed: true,
  single_meal_suggestion_completed: true,
  recipe_viewed: true,
  recipe_imported: true,
  recipe_import_failed: true,
  grocery_list_opened: true,
  grocery_list_cleared: true,
  grocery_list_completed: true,
  grocery_item_toggled: true,
  grocery_item_removed: true,
  grocery_item_restored: true,
  grocery_item_quantity_edited: true,
  grocery_item_added: true,
  meal_swapped: true,
  dashboard_hero_viewed: true,
  dashboard_hero_swap_clicked: true,
  meal_rated: true,
  meal_cooked: true,
  favorite_toggled: true,
  view_changed: true,
  invite_claimed: true,
  invite_claim_failed: true,
  feedback_submitted: true,
  pmf_survey_submitted: true,
  retailer_picker_opened: true,
  retailer_selected: true,
  retailer_linked: true,
  retailer_link_failed: true,
  match_review_viewed: true,
  match_swapped: true,
  match_excluded: true,
  cart_pushed: true,
  handoff_link_opened: true,
  kroger_cart_pushed: true,
  kroger_cart_push_failed: true,
  aisle_order_changed: true,
  onboarding_step_completed: true,
  pantry_scan_attempted: true,
  pantry_scan_succeeded: true,
  pantry_scan_failed: true,
  pantry_intake_reviewed: true,
  recipe_import_reviewed: true,
  server_signup_blocked: true,
  server_plan_utilization_predicted: true,
};
export type ServerAnalyticsEvent = Extract<AnalyticsEvent, `server_${string}`>;
export type AnalyticsArguments<E extends AnalyticsEvent> =
  AnalyticsEventProperties[E] extends undefined
    ? [event: E]
    : [event: E, properties: AnalyticsEventProperties[E]];
export interface AnalyticsContext {
  distinctId: string;
  householdId: string | null;
}
export interface AnalyticsPersonProperties {
  householdAdults: number;
  householdKids: number;
  skillLevel: "beginner" | "intermediate" | "advanced";
  signupWeek: string;
  analyticsCohort: AnalyticsCohort;
}

export const DENIED_ANALYTICS_PROPERTY_KEY =
  /allerg|diet|email|name|feedback|freetext|^(?:user|distinct|household)_?id$/i;
function hasDeniedAnalyticsPropertyKeyInValue(
  value: unknown,
  seen: WeakSet<object>,
): boolean {
  try {
    if (
      value instanceof Error ||
      (value !== null &&
        typeof value === "object" &&
        Object.prototype.toString.call(value) === "[object Error]")
    )
      return true;
    if (Array.isArray(value)) {
      if (seen.has(value)) return true;
      seen.add(value);
      return value.some((child) =>
        hasDeniedAnalyticsPropertyKeyInValue(child, seen),
      );
    }
    if (!value || typeof value !== "object") return false;
    if (seen.has(value)) return true;
    seen.add(value);
    return Object.entries(value).some(
      ([key, child]) =>
        DENIED_ANALYTICS_PROPERTY_KEY.test(key) ||
        hasDeniedAnalyticsPropertyKeyInValue(child, seen),
    );
  } catch {
    return true;
  }
}
export function hasDeniedAnalyticsPropertyKey(value: unknown): boolean {
  return hasDeniedAnalyticsPropertyKeyInValue(value, new WeakSet<object>());
}
export function validateAnalyticsProperties(value: unknown): boolean {
  return (
    value === undefined ||
    (!!value &&
      typeof value === "object" &&
      !hasDeniedAnalyticsPropertyKey(value))
  );
}

const PANTRY_SCAN_MODES = new Set(["shelf", "receipt", "barcode"]);
const CAPTURE_METHODS = new Set(["camera", "manual"]);
const RESOLVED_VIA_VALUES = new Set(["local", "kroger", "fdc", "plu", "none"]);

function isAggregateCount(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0;
}

function hasExactKeys(
  props: Record<string, unknown>,
  expectedKeys: readonly string[],
): boolean {
  const keys = Object.keys(props);
  return (
    keys.length === expectedKeys.length &&
    expectedKeys.every((key) => Object.hasOwn(props, key))
  );
}

/** Like hasExactKeys, but `optionalKeys` may or may not be present -- used
 * for pantry_scan_succeeded's barcode shape, whose `resolvedVia` this slice
 * never actually sends (see that property's doc comment) but whose type
 * still allows a future caller to. */
function hasKeysWithin(
  props: Record<string, unknown>,
  requiredKeys: readonly string[],
  optionalKeys: readonly string[],
): boolean {
  const keys = Object.keys(props);
  return (
    requiredKeys.every((key) => Object.hasOwn(props, key)) &&
    keys.every(
      (key) => requiredKeys.includes(key) || optionalKeys.includes(key),
    )
  );
}

function isProvenanceCounts(
  value: unknown,
): value is Record<MatchProvenance, number> {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    hasExactKeys(record, MATCH_PROVENANCE_VALUES) &&
    MATCH_PROVENANCE_VALUES.every((key) => isAggregateCount(record[key]))
  );
}

const INTAKE_EVENT_VALIDATORS: Record<string, (props: Record<string, unknown>) => boolean> = {
  pantry_scan_attempted: (props) => {
    if (props.mode === "barcode") {
      return (
        hasExactKeys(props, ["mode", "cameraSupported", "captureMethod"]) &&
        typeof props.cameraSupported === "boolean" &&
        CAPTURE_METHODS.has(props.captureMethod as string)
      );
    }
    return (
      hasExactKeys(props, ["mode"]) &&
      (props.mode === "shelf" || props.mode === "receipt")
    );
  },
  pantry_scan_succeeded: (props) => {
    if (!isAggregateCount(props.itemCount)) return false;
    if (props.mode === "barcode") {
      return (
        hasKeysWithin(
          props,
          ["mode", "itemCount", "codeFamily"],
          ["resolvedVia"],
        ) &&
        BARCODE_FAMILY_VALUES.includes(props.codeFamily as BarcodeFamily) &&
        (props.resolvedVia === undefined ||
          RESOLVED_VIA_VALUES.has(props.resolvedVia as string))
      );
    }
    return (
      hasExactKeys(props, ["mode", "itemCount"]) &&
      (props.mode === "shelf" || props.mode === "receipt")
    );
  },
  pantry_scan_failed: (props) =>
    hasExactKeys(props, ["mode", "status"]) &&
    PANTRY_SCAN_MODES.has(props.mode as string) &&
    isAggregateCount(props.status),
  pantry_intake_reviewed: (props) =>
    hasExactKeys(props, [
      "mode",
      "acceptedCount",
      "editedCount",
      "relabeledCount",
      "rejectedCount",
      "provenanceCounts",
    ]) &&
    PANTRY_SCAN_MODES.has(props.mode as string) &&
    isAggregateCount(props.acceptedCount) &&
    isAggregateCount(props.editedCount) &&
    isAggregateCount(props.relabeledCount) &&
    isAggregateCount(props.rejectedCount) &&
    isProvenanceCounts(props.provenanceCounts),
  recipe_import_reviewed: (props) =>
    hasExactKeys(props, ["acceptedCount", "editedCount"]) &&
    props.acceptedCount === 1 &&
    (props.editedCount === 0 || props.editedCount === 1),
};

/** Shared closed payload validation; malformed telemetry never breaks a user action. */
export function validateIntakeAnalyticsProperties(event: string, value: unknown): boolean {
  try {
    if (!Object.hasOwn(INTAKE_EVENT_VALIDATORS, event) || !value || typeof value !== "object" || Array.isArray(value) || !validateAnalyticsProperties(value)) return false;
    return INTAKE_EVENT_VALIDATORS[event]!(value as Record<string, unknown>);
  } catch {
    return false;
  }
}
